import { useMemo } from 'react';
import { Col, Table, Popover, Typography } from 'antd';

const { Title } = Typography;

const Statistics = ({ columns, courses: coursesMap, rsSkillsGroup, rsProfiles, semestersToShow, selectedProfiles }) => {
  const courses = useMemo(() => {
    const out = [];
    ['1-w', '1-s', '2-w', '2-s', '3-w', '3-s', '4-w', '4-s', '5-w', '5-s'].forEach((colId) => {
      (columns[colId]?.courseIds || []).forEach((cid) => {
        const c = coursesMap[cid];
        if (c) out.push({ ...c, position: colId });
      });
    });
    return out;
  }, [columns, coursesMap]);

  const ectsSum = useMemo(() => {
    const sums = {
      '1-w': 0,
      '1-s': 0,
      '2-w': 0,
      '2-s': 0,
      '3-w': 0,
      '3-s': 0,
      '4-w': 0,
      '4-s': 0,
      '5-w': 0,
      '5-s': 0,
      sum: 0,
    };
    courses.forEach((c) => {
      sums[c.position] += c.credits || 0;
      sums.sum += c.credits || 0;
    });
    return sums;
  }, [courses]);

  const selectedSkillIds = useMemo(() => {
    const set = new Set();
    courses.forEach((c) => (c.skills_group || []).forEach((sg) => set.add(sg)));
    return Array.from(set).sort((a, b) => a - b);
  }, [courses]);

  const enisaProfileCoverage = useMemo(() => {
    return rsProfiles.map((p) => {
      const included = [],
        missing = [];
      (p.skills_group || []).forEach((sg) => {
        (selectedSkillIds.includes(sg) ? included : missing).push(sg);
      });
      const coverage = Math.round((included.length / (p.skills_group.length || 1)) * 100);
      return { id: p.id, name: p.name, coverage, included, missing };
    });
  }, [rsProfiles, selectedSkillIds]);

  const missingSkills = useMemo(() => {
    const miss = new Set();
    enisaProfileCoverage.filter((p) => selectedProfiles.includes(p.id)).forEach((p) => p.missing.forEach((sg) => miss.add(sg)));
    const arr = Array.from(miss).sort((a, b) => a - b);
    return arr.length ? (
      arr.map((id) => (
        <div className="work-role" key={id}>
          • {rsSkillsGroup.find((e) => e.id === id)?.name || id}
        </div>
      ))
    ) : (
      <div className="work-role">None</div>
    );
  }, [enisaProfileCoverage, selectedProfiles, rsSkillsGroup]);

  const percentageLevel = (pct) => (pct > 67 ? 'High' : pct > 33 ? 'Medium' : pct >= 0 ? 'Low' : 'Error');

  const ectsData = [
    {
      key: '1',
      year: '1st year',
      winter: ectsSum['1-w'],
      summer: semestersToShow >= 2 ? ectsSum['1-s'] : '-',
      sum: ectsSum['1-w'] + ectsSum['1-s'],
    },
    semestersToShow >= 3 && {
      key: '2',
      year: '2nd year',
      winter: ectsSum['2-w'],
      summer: semestersToShow >= 4 ? ectsSum['2-s'] : '-',
      sum: ectsSum['2-w'] + ectsSum['2-s'],
    },
    semestersToShow >= 5 && {
      key: '3',
      year: '3rd year',
      winter: ectsSum['3-w'],
      summer: semestersToShow >= 6 ? ectsSum['3-s'] : '-',
      sum: ectsSum['3-w'] + ectsSum['3-s'],
    },
    semestersToShow >= 7 && {
      key: '4',
      year: '4th year',
      winter: ectsSum['4-w'],
      summer: semestersToShow >= 8 ? ectsSum['4-s'] : '-',
      sum: ectsSum['4-w'] + ectsSum['4-s'],
    },
    semestersToShow >= 9 && {
      key: '5',
      year: '5th year',
      winter: ectsSum['5-w'],
      summer: semestersToShow >= 10 ? ectsSum['5-s'] : '-',
      sum: ectsSum['5-w'] + ectsSum['5-s'],
    },
    {
      key: 'sum',
      year: 'Total',
      winter: '',
      summer: '',
      sum: ectsSum.sum,
    },
  ].filter(Boolean);

  const ectsColumns = [
    { title: '', dataIndex: 'year', key: 'year', align: 'center' },
    { title: 'Winter', dataIndex: 'winter', key: 'winter', align: 'center' },
    { title: 'Summer', dataIndex: 'summer', key: 'summer', align: 'center' },
    { title: 'Sum', dataIndex: 'sum', key: 'sum', render: (text) => <div className="sum-text">{text}</div>, align: 'center' },
  ];

  const profileColumns = [
    { title: 'Profile Name', dataIndex: 'name', key: 'name' },
    {
      title: 'Coverage',
      dataIndex: 'coverage',
      key: 'coverage',
      align: 'center',
      onCell: (rec) => ({
        className: rec.coverage === 100 ? 'green' : 'normal',
      }),

      render: (percent, rec) => (
        <Popover
          placement="leftBottom"
          arrowPointAtCenter
          getPopupContainer={(trigger) => trigger.parentElement}
          trigger="hover"
          content={
            <div className="skills-popover">
              <Title level={4}>{rec.name}</Title>
              <Title level={5}>Included Skills</Title>
              <ul>{rec.included.length > 0 ? rec.included.map((id, i) => <li key={i}>{rsSkillsGroup.find((e) => e.id === id)?.name || id}</li>) : <span className="empty">None</span>}</ul>
              <Title level={5}>Missing Skills</Title>
              <ul>{rec.missing.length > 0 ? rec.missing.map((id, i) => <li key={i}>{rsSkillsGroup.find((e) => e.id === id)?.name || id}</li>) : <span className="empty">None</span>}</ul>
            </div>
          }
        >
          <div className="coverage-text">
            {percentageLevel(percent)}
          </div>
        </Popover>
      )
    },
  ];

  return (
    <Col xs={24} lg={24} xl={8} className="base-statistics">
      <h3 className="section-title">Statistics</h3>
      <div className="base-statistics-content">
        {courses.length > 0 ? (
          <div className="base-statistics-content-scroll">
            <h6>Total ECTS credits:</h6>
            <Table size="small" pagination={false} dataSource={ectsData} columns={ectsColumns} />

            <h6>ENISA Profiles coverage:</h6>
            <Table size="small" pagination={false} dataSource={enisaProfileCoverage} columns={profileColumns} rowClassName={(rec) => (selectedProfiles.includes(rec.id) ? 'selected' : '')} />

            <h6>Missing skills to cover selected profiles:</h6>
            {missingSkills}
          </div>
        ) : (
          <div className="empty-data">
            <span className="header">Your curricula is empty.</span>
            <br />
            Perform an analysis and see your curricula.
          </div>
        )}
      </div>
    </Col>
  );
};

export default Statistics;
